// @flow

import React, { Component, type Node } from 'react';
import cx from 'classnames';
import Translate from 'client/components/translate';
import type { TranslateType } from 'types/translateType.flow';
import Input, { inputDefaultProps, type InputProps } from './input';

export type InputGroupProps = {
  ...$Exact<InputProps>,
  children: Node,
  containerClassName: string,
  error: TranslateType | false | null,
  info: TranslateType | false | null,
  inlineButton?: Node,
  label: TranslateType | null,
  labelText: string | null,
  noLabelAnimate: boolean,
  suppressError: boolean,
  symbol: string | Node | null,
};

export const inputGroupDefaultProps = {
  ...inputDefaultProps,
  children: null,
  containerClassName: '',
  error: false,
  inlineButton: null,
  label: null,
  labelText: null,
  info: null,
  suppressError: false,
  noLabelAnimate: false,
  symbol: null,
};

type State = {
  touched: boolean,
  focus: boolean,
};

export default class InputGroup extends Component<InputGroupProps, State> {
  static defaultProps = inputGroupDefaultProps;

  state = {
    touched: false,
    focus: false,
  };

  onBlur = (event: SyntheticEvent<>) => {
    this.setState({
      focus: false,
    });

    return this.props.onBlur(event);
  };

  onFocus = (event: SyntheticEvent<>) => {
    this.setState({
      touched: true,
      focus: true,
    });

    return this.props.onFocus(event);
  };

  render() {
    const {
      children,
      error,
      info,
      inlineButton,
      label,
      labelText,
      onBlur,
      onFocus,
      className,
      containerClassName,
      suppressError,
      symbol,
      noLabelAnimate,
      placeholder, // label animation suppresses placeholder until input is focused
      ...inputProps
    } = this.props;

    const groupClassName = this.props.children ? 'input-group' : '';
    const inputContainerClassName = cx('input-container', containerClassName, {
      ...this.state,
      error,
      info,
      empty: !this.props.value && this.props.value !== 0 && !noLabelAnimate,
      'with-symbol': symbol,
    });

    return (
      <div className={inputContainerClassName}>
        {(label || labelText) && (
          <label htmlFor={this.props.id}>{labelText || <Translate {...label} />}</label>
        )}
        <div className={groupClassName}>
          <div className="input-wrapper flex-row">
            {symbol ? <div className="pad-left symbol">{symbol}</div> : ''}
            <Input
              {...inputProps}
              placeholder={placeholder}
              className={cx(className, { 'input-group-field': this.props.children })}
              onBlur={this.onBlur}
              onFocus={this.onFocus} />
            {inlineButton ? <div>{inlineButton}</div> : ''}
          </div>
          {children && <div className="input-group-button">{children}</div>}
        </div>
        {info ? (
          <div className="info-message">{info && <Translate {...info} />}</div>
        ) : (
          <div className="error-message">{error && !suppressError && <Translate {...error} />}</div>
        )}
      </div>
    );
  }
}
